import * as React from "react";
import { browserHistory } from "react-router";
import * as Tgcc from "../../tgccGlobal";
import { MajorAccountsHdr } from "../MajorAccountsHdr";
import { EstimateErrorPopup, EstimateConfirmationPopup } from "../ErrorPopupModal";
import { fetchArtFeePaymentLinkFromToken, ArtFeePaymentLinkFromToken } from "./paymentLinksClient";
import {
    ApiError,
    CardInput,
    ChargeResult,
    SavedCard,
    SavedBankAccount,
    chargeOneTimeBank,
    chargeSavedBank,
    chargeOneTimeToken,
    chargeSavedCard,
    fetchSavedPaymentMethods,
    getFailedChargeUserMessage,
    saveBankOnFile,
    saveCardOnFile,
    tokenizeBankInBrowser,
    tokenizeCardInBrowser,
} from "../Estimates/qbPaymentsClient";
import { BackendCardFundingType, resolveCardFundingTypeFromCardNumber } from "../Estimates/handyBinLookup";

import * as FaIcons from "react-icons/fa";

/** Matches TGCC card processing fee line (not applied to debit). */
const CARD_PROCESSING_FEE_RATE = 0.03;

type BinPreviewPhase = "short" | "loading" | "success" | "unknown";

function normalizeStoredCardFunding(raw?: string | null): BackendCardFundingType | "unknown" {
    if (raw == null || String(raw).trim() === "") return "unknown";
    const n = String(raw).trim().replace(/[\s_]/g, "").toLowerCase();
    if (n === "debit") return "Debit";
    if (n === "credit") return "Credit";
    if (n === "prepaid") return "Prepaid";
    return "unknown";
}

function fundingLabel(f: BackendCardFundingType | "unknown"): string {
    if (f === "unknown") return "Not verified";
    if (f === "Debit") return "Debit";
    if (f === "Credit") return "Credit";
    return "Prepaid";
}

function fundingBadgeStyle(f: BackendCardFundingType | "unknown"): React.CSSProperties {
    const base: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        fontSize: "0.68rem",
        fontWeight: 800,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        padding: "0.2rem 0.45rem",
        borderRadius: "999px",
        border: "1px solid transparent",
    };
    if (f === "Debit") return { ...base, background: "#d1fae5", color: "#047857", borderColor: "#6ee7b7" };
    if (f === "Credit") return { ...base, background: "#e0e7ff", color: "#3730a3", borderColor: "#a5b4fc" };
    if (f === "Prepaid") return { ...base, background: "#fef3c7", color: "#b45309", borderColor: "#fcd34d" };
    return { ...base, background: "#f1f5f9", color: "#475569", borderColor: "#cbd5e1" };
}

const ART_FEE_PAY_SAVED_CARDS_KEY = "ArtFeePaySavedCards";
const ART_FEE_PAY_SAVED_BANK_KEY = "ArtFeePaySavedBankAccounts";

/** Backend may send `isDefault`; keep explicit so TS is satisfied even if the client type lags. */
type SavedBankAccountWithOptionalDefault = SavedBankAccount & { isDefault?: boolean };

interface ArtFeePayProps {
    location: {
        query: {
            token?: string;
        };
    };
}

interface ArtFeePayState {
    webOrderNumber: string | null;
    customerName: string | null;
    customerFirstName: string | null;
    maxPayableAmount: number | null;
    linkStatus: string | null;
    isLoading: boolean;
    errorMessage: string | null;
    showConfirmation?: boolean;
    customerId: string;
    amount: string;
    currency: string;
    description: string;
    paymentMode: "new" | "saved";
    paymentRail: "card" | "ach";
    saveCardForFuture: boolean;
    achMode: "new" | "saved";
    saveBankForFuture: boolean;
    selectedSavedCardId: string;
    selectedSavedBankAccountId: string;
    savedCards: SavedCard[];
    savedBankAccounts: SavedBankAccount[];
    cardInput: CardInput;
    processingStep: string | null;
    paymentError: string | null;
    chargeResult: ChargeResult | null;
    paymentId: string | null;
    cardId: string | null;
    isPaymentProcessing: boolean;
    achInput: {
        accountHolderName: string;
        routingNumber: string;
        accountNumber: string;
        accountType: "checking" | "savings";
    };
    /** BIN lookup for new card: drives fee preview (debit vs credit). */
    binPreviewPhase: BinPreviewPhase;
    binPreviewFunding?: BackendCardFundingType;
}

class ArtFeePay extends React.Component<ArtFeePayProps, ArtFeePayState> {
    private _binDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    private _binLookupSeq = 0;
    private paymentErrorBannerRef = React.createRef<HTMLDivElement>();
    getRawDataBlockedMessage = (): string =>
        "Payment tokenization provider is not available. Card and bank details must be tokenized directly with Intuit before submitting.";

    getTokenPreview = (token?: string | null): string | null => {
        if (!token) return null;
        const trimmed = token.trim();
        if (!trimmed) return null;
        return trimmed.length <= 8 ? trimmed : `${trimmed.slice(0, 8)}...`;
    };

    logCardSaveFlow = (message: string, details?: Record<string, unknown>) => {
        console.info("[ArtFeePay][saved-card]", message, details || {});
    };

    getWebOrderTokenValue = (): string => {
        return this.props.location?.query?.token || "";
    };

    refreshSavedCardsFromBackend = async () => {
        const webOrderToken = this.getWebOrderTokenValue();
        if (!webOrderToken) return;
        if (this.state.isPaymentProcessing) return;
        try {
            const savedMethodsRes = await fetchSavedPaymentMethods(webOrderToken);
            const qbCustomerId = savedMethodsRes.qbCustomerId;
            const cards = savedMethodsRes.cards;
            const accounts = savedMethodsRes.accounts;
            this.saveSavedCards(cards, webOrderToken);
            this.saveSavedBankAccounts(accounts, webOrderToken);
            this.setState((prev) => {
                const base = {
                    customerId: qbCustomerId || prev.customerId,
                    savedCards: cards,
                    savedBankAccounts: accounts,
                };
                // If the user started paying while this request was in flight, only sync lists —
                // do not reset rail/mode/selection via getDefaultPaymentPreferences.
                if (prev.isPaymentProcessing) {
                    return base;
                }
                return { ...base, ...this.getDefaultPaymentPreferences(cards, accounts) };
            });
        } catch (err) {
            // Best-effort fallback: keep whatever we already have locally
            console.error("Failed to fetch saved cards:", err);
            const fallbackCards = this.loadSavedCards(webOrderToken);
            const fallbackAccounts = this.loadSavedBankAccounts(webOrderToken);
            this.setState((prev) => {
                const base = {
                    savedCards: fallbackCards,
                    savedBankAccounts: fallbackAccounts,
                };
                if (prev.isPaymentProcessing) {
                    return base;
                }
                return { ...base, ...this.getDefaultPaymentPreferences(fallbackCards, fallbackAccounts) };
            });
        }
    };

    getWebOrderAuth = (): { webOrderToken?: string; webOrderNumber?: string } | undefined => {
        const webOrderToken = this.props.location?.query?.token;
        if (!webOrderToken) return undefined;
        const webOrderNumber = this.state.webOrderNumber;
        if (!webOrderNumber) return undefined;
        return { webOrderToken, webOrderNumber };
    };

    pickPreferredSavedCardId = (cards: SavedCard[]): string => {
        const preferred = cards.find((c) => c.isDefault);
        return (preferred || cards[0])?.cardId || "";
    };

    pickPreferredSavedBankAccountId = (accounts: SavedBankAccountWithOptionalDefault[]): string => {
        const preferred = accounts.find((a) => a.isDefault);
        return (preferred || accounts[0])?.bankAccountId || "";
    };

    /** When only one kind exists, open that rail + saved tab; when both exist, prefer card. */
    getDefaultPaymentPreferences = (
        cards: SavedCard[],
        accounts: SavedBankAccountWithOptionalDefault[]
    ): Pick<
        ArtFeePayState,
        "paymentRail" | "paymentMode" | "achMode" | "selectedSavedCardId" | "selectedSavedBankAccountId"
    > => {
        const hasCards = cards.length > 0;
        const hasBank = accounts.length > 0;
        const cardId = this.pickPreferredSavedCardId(cards);
        const bankId = this.pickPreferredSavedBankAccountId(accounts);

        if (hasCards && hasBank) {
            return {
                paymentRail: "card",
                paymentMode: "saved",
                achMode: "saved",
                selectedSavedCardId: cardId,
                selectedSavedBankAccountId: bankId,
            };
        }
        if (hasCards) {
            return {
                paymentRail: "card",
                paymentMode: "saved",
                achMode: "new",
                selectedSavedCardId: cardId,
                selectedSavedBankAccountId: "",
            };
        }
        if (hasBank) {
            return {
                paymentRail: "ach",
                paymentMode: "new",
                achMode: "saved",
                selectedSavedCardId: "",
                selectedSavedBankAccountId: bankId,
            };
        }
        return {
            paymentRail: "card",
            paymentMode: "new",
            achMode: "new",
            selectedSavedCardId: "",
            selectedSavedBankAccountId: "",
        };
    };

    constructor(props: ArtFeePayProps) {
        super(props);
        const urlToken = (props.location?.query?.token || "").trim();
        const initialSavedCards = this.loadSavedCards(urlToken);
        const initialSavedBankAccounts = this.loadSavedBankAccounts(urlToken);
        this.state = {
            webOrderNumber: null,
            customerName: null,
            customerFirstName: null,
            maxPayableAmount: null,
            linkStatus: null,
            isLoading: true,
            errorMessage: null,
            showConfirmation: false,
            customerId: this.getDefaultCustomerId(),
            amount: "",
            currency: "USD",
            description: "Art Design Fee",
            saveCardForFuture: true,
            saveBankForFuture: true,
            savedCards: initialSavedCards,
            savedBankAccounts: initialSavedBankAccounts,
            ...this.getDefaultPaymentPreferences(initialSavedCards, initialSavedBankAccounts),
            cardInput: {
                number: "",
                expMonth: 0,
                expYear: 0,
                cvc: "",
                name: "",
                postalCode: "",
            },
            processingStep: null,
            paymentError: null,
            chargeResult: null,
            paymentId: null,
            cardId: null,
            isPaymentProcessing: false,
            achInput: {
                accountHolderName: "",
                routingNumber: "",
                accountNumber: "",
                accountType: "checking",
            },
            binPreviewPhase: "short",
            binPreviewFunding: undefined,
        };
    }

    componentDidUpdate(_prevProps: ArtFeePayProps, prevState: ArtFeePayState): void {
        if (this.state.paymentError && this.state.paymentError !== prevState.paymentError) {
            const el = this.paymentErrorBannerRef.current;
            if (el && typeof el.scrollIntoView === "function") {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }

    componentWillUnmount(): void {
        if (this._binDebounceTimer) {
            clearTimeout(this._binDebounceTimer);
            this._binDebounceTimer = null;
        }
        this._binLookupSeq++;
    }

    clearBinFundingPreview = (): void => {
        if (this._binDebounceTimer) {
            clearTimeout(this._binDebounceTimer);
            this._binDebounceTimer = null;
        }
        this._binLookupSeq++;
        this.setState({ binPreviewPhase: "short", binPreviewFunding: undefined });
    };

    updateBinPreviewFromNumber = (cardNumber: string): void => {
        if (this.state.paymentRail !== "card" || this.state.paymentMode !== "new") return;
        const digits = String(cardNumber || "").replace(/\D/g, "");
        if (digits.length < 8) {
            if (this._binDebounceTimer) {
                clearTimeout(this._binDebounceTimer);
                this._binDebounceTimer = null;
            }
            this.setState({ binPreviewPhase: "short", binPreviewFunding: undefined });
            return;
        }
        this.setState({ binPreviewPhase: "loading" });
        if (this._binDebounceTimer) clearTimeout(this._binDebounceTimer);
        this._binDebounceTimer = setTimeout(async () => {
            this._binDebounceTimer = null;
            const seq = ++this._binLookupSeq;
            const funding = await resolveCardFundingTypeFromCardNumber(cardNumber);
            if (seq !== this._binLookupSeq) return;
            if (this.state.paymentRail !== "card" || this.state.paymentMode !== "new") return;
            if (funding) {
                this.setState({ binPreviewPhase: "success", binPreviewFunding: funding });
            } else {
                this.setState({ binPreviewPhase: "unknown", binPreviewFunding: undefined });
            }
        }, 420);
    };

    /** Used for fee display and charge total (debit → $0 processing fee). */
    getEffectiveFundingForFee = (): BackendCardFundingType | "unknown" => {
        if (this.state.paymentRail !== "card") return "unknown";
        if (this.state.paymentMode === "saved") {
            const c = this.state.savedCards.find((x) => x.cardId === this.state.selectedSavedCardId);
            return normalizeStoredCardFunding(c?.cardFundingType);
        }
        if (this.state.binPreviewPhase === "success" && this.state.binPreviewFunding) {
            return this.state.binPreviewFunding;
        }
        return "unknown";
    };

    getDefaultCustomerId = (): string => {
        try {
            const raw = localStorage.getItem("LoginData");
            if (!raw) return "1";
            const parsed = JSON.parse(raw);
            return parsed?.id ? String(parsed.id) : "1";
        } catch (err) {
            return "1";
        }
    };

    /** Scoped by art-fee link token so shared browsers do not reuse another customer's saved-method display metadata. */
    savedCardsStorageKey = (webOrderToken: string): string | null => {
        const t = webOrderToken.trim();
        if (!t) return null;
        return `${ART_FEE_PAY_SAVED_CARDS_KEY}:${t}`;
    };

    savedBankAccountsStorageKey = (webOrderToken: string): string | null => {
        const t = webOrderToken.trim();
        if (!t) return null;
        return `${ART_FEE_PAY_SAVED_BANK_KEY}:${t}`;
    };

    loadSavedCards = (webOrderToken: string): SavedCard[] => {
        const key = this.savedCardsStorageKey(webOrderToken);
        if (!key) return [];
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return [];
            const cards = JSON.parse(raw);
            return Array.isArray(cards) ? cards : [];
        } catch {
            return [];
        }
    };

    saveSavedCards = (cards: SavedCard[], webOrderToken: string) => {
        const key = this.savedCardsStorageKey(webOrderToken);
        if (!key) return;
        try {
            localStorage.setItem(key, JSON.stringify(cards));
        } catch {
            /* ignore quota / private mode */
        }
    };

    loadSavedBankAccounts = (webOrderToken: string): SavedBankAccount[] => {
        const key = this.savedBankAccountsStorageKey(webOrderToken);
        if (!key) return [];
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return [];
            const accounts = JSON.parse(raw);
            return Array.isArray(accounts) ? accounts : [];
        } catch {
            return [];
        }
    };

    saveSavedBankAccounts = (accounts: SavedBankAccount[], webOrderToken: string) => {
        const key = this.savedBankAccountsStorageKey(webOrderToken);
        if (!key) return;
        try {
            localStorage.setItem(key, JSON.stringify(accounts));
        } catch {
            /* ignore quota / private mode */
        }
    };

    componentDidMount() {
        // Ensure global styles are injected once (SSR-safe)
        if (typeof document !== 'undefined') {
            if (!document.getElementById(GLOBAL_STYLE_ID)) {
                const styleElement = document.createElement("style");
                styleElement.id = GLOBAL_STYLE_ID;
                styleElement.innerHTML = globalStyles;
                document.head.appendChild(styleElement);
            }
        }
        const tokenFromUrl = this.props.location?.query?.token;

        if (!tokenFromUrl) {
            this.setState({
                errorMessage: "Invalid or expired payment link.",
                isLoading: false,
            });
            return;
        }

        this.fetchLinkDetails(tokenFromUrl);
    }

    formatLinkAmount = (link: ArtFeePaymentLinkFromToken): string => {
        if (typeof link.amount === "number" && Number.isFinite(link.amount)) {
            return link.amount.toFixed(2);
        }
        return "";
    };

    paymentDescriptionForLink = (link: ArtFeePaymentLinkFromToken): string => {
        return `Art Design Fee - ${link.webOrderNumber}`;
    };

    fetchLinkDetails = (token: string) => {
        fetchArtFeePaymentLinkFromToken(
            token,
            (link) => {
                const amount = this.formatLinkAmount(link);
                this.setState(
                    {
                        webOrderNumber: link.webOrderNumber,
                        customerName: link.customerName || null,
                        customerFirstName: link.customerFirstName || link.customerName || null,
                        maxPayableAmount:
                            typeof link.maxPayableAmount === "number" && Number.isFinite(link.maxPayableAmount)
                                ? link.maxPayableAmount
                                : null,
                        linkStatus: typeof link.status === "string" && link.status.trim() ? link.status.trim() : null,
                        amount: amount || "0.00",
                        description: this.paymentDescriptionForLink(link),
                        isLoading: false,
                        errorMessage: null,
                    },
                    () => {
                        this.refreshSavedCardsFromBackend();
                    }
                );
            },
            (error) => {
                this.setState({
                    errorMessage: "Invalid or expired payment link.",
                    isLoading: false,
                });
                console.error("Error fetching art fee payment link:", error);
            }
        );
    };

    setPaymentMode = (mode: "new" | "saved") => {
        this.setState({ paymentMode: mode, paymentError: null }, () => {
            if (mode === "saved") {
                this.clearBinFundingPreview();
            } else {
                this.updateBinPreviewFromNumber(this.state.cardInput.number);
            }
        });
    };

    setPaymentRail = (rail: "card" | "ach") => {
        this.setState({ paymentRail: rail, paymentError: null, processingStep: null }, () => {
            if (rail === "ach") {
                this.clearBinFundingPreview();
            } else if (this.state.paymentMode === "new") {
                this.updateBinPreviewFromNumber(this.state.cardInput.number);
            }
        });
    };

    setAchMode = (mode: "new" | "saved") => {
        this.setState({ achMode: mode, paymentError: null });
    };

    onCardInputChange = (field: keyof CardInput, value: string | number) => {
        this.setState(
            (prev) => ({
                cardInput: {
                    ...prev.cardInput,
                    [field]: value,
                },
            }),
            () => {
                if (field === "number") {
                    this.updateBinPreviewFromNumber(this.state.cardInput.number);
                }
            }
        );
    };

    onAchInputChange = (field: "accountHolderName" | "routingNumber" | "accountNumber" | "accountType", value: string) => {
        this.setState((prev) => ({
            achInput: {
                ...prev.achInput,
                [field]: value,
            },
        }));
    };

    getParsedAmount = (): number => Number(this.state.amount) || 0;

    getProcessingFee = (): number => {
        const amount = this.getParsedAmount();
        if (!amount || amount <= 0) return 0;
        if (this.state.paymentRail === "ach") {
            return 0;
        }
        if (this.getEffectiveFundingForFee() === "Debit") {
            return 0;
        }
        return amount * CARD_PROCESSING_FEE_RATE;
    };

    getTotalDue = (): number => this.getParsedAmount() + this.getProcessingFee();

    getMaxPayableAmount = (): number | null => {
        const max = this.state.maxPayableAmount;
        if (max == null || !Number.isFinite(max)) return null;
        return max;
    };

    isNonPayableStatus = (status: string | null | undefined): boolean => {
        if (!status) return false;
        const normalized = status.trim().toLowerCase();
        return (
            normalized === "paid" ||
            normalized === "cancelled" ||
            normalized === "canceled" ||
            normalized === "expired" ||
            normalized === "refunded" ||
            normalized === "draft" ||
            normalized === "paying"
        );
    };

    isLinkPayable = (): boolean => {
        if (this.isNonPayableStatus(this.state.linkStatus)) return false;
        const max = this.getMaxPayableAmount();
        if (max === 0) return false;
        return true;
    };

    getNotPayableMessage = (): string => {
        const status = (this.state.linkStatus || "").trim().toLowerCase();
        if (status === "paid") return "This art design fee has already been paid.";
        if (status === "cancelled" || status === "canceled") return "This payment link has been cancelled.";
        if (status === "expired") return "This payment link has expired.";
        if (status === "refunded") return "This art design fee was refunded and is no longer payable.";
        if (status === "paying") {
            return "A payment is already in progress for this link. Please wait, or contact us if you need help.";
        }
        if (status === "draft") return "This payment link is not ready yet.";
        if (this.getMaxPayableAmount() === 0) {
            return "This payment link has no remaining balance to pay.";
        }
        return "This payment link is no longer payable.";
    };

    validateChargeAmountWithinLimit = (totalChargeAmount: number): string | null => {
        if (!this.isLinkPayable()) {
            return this.getNotPayableMessage();
        }
        const max = this.getMaxPayableAmount();
        if (max == null) return null;
        if (totalChargeAmount > max + 0.001) {
            return `Payment amount ($${totalChargeAmount.toFixed(2)}) exceeds the maximum allowed ($${max.toFixed(2)}).`;
        }
        return null;
    };

    finalizeSuccessfulPayment = (charge: ChargeResult, extraState: Partial<ArtFeePayState> = {}) => {
        const failedMsg = getFailedChargeUserMessage(charge);
        if (failedMsg) {
            this.setState({
                isPaymentProcessing: false,
                processingStep: null,
                paymentError: failedMsg,
                chargeResult: null,
                paymentId: null,
            });
            return;
        }
        this.setState((prev) => ({
            ...prev,
            isPaymentProcessing: false,
            processingStep: null,
            showConfirmation: true,
            chargeResult: charge,
            paymentId: charge.paymentId || null,
            maxPayableAmount: 0,
            linkStatus: "Paid",
            ...extraState,
        }));
        this.refreshSavedCardsFromBackend();
    };

    getCardTotalDueForFunding = (funding: BackendCardFundingType | "unknown"): number => {
        const amount = this.getParsedAmount();
        if (!amount || amount <= 0) return 0;
        if (funding === "Debit") return amount;
        return amount + amount * CARD_PROCESSING_FEE_RATE;
    };

    getPaymentErrorText = (error: ApiError | any): string => {
        const body = error?.body;
        const parts: string[] = [];
        const pushUnique = (value: unknown) => {
            if (typeof value !== "string") return;
            const trimmed = value.trim();
            if (!trimmed) return;
            const normalized = trimmed.replace(/\.+$/, "").toLowerCase();
            if (parts.some((existing) => existing.replace(/\.+$/, "").toLowerCase() === normalized)) {
                return;
            }
            parts.push(trimmed);
        };
        pushUnique(error?.message);
        if (typeof body === "string") pushUnique(body);
        pushUnique(body?.message);
        pushUnique(body?.providerError);
        if (Array.isArray(body?.errors)) {
            body.errors.forEach((item: any) => {
                if (typeof item === "string") pushUnique(item);
                pushUnique(item?.message);
            });
        }
        return this.toCustomerFacingPaymentError(parts.join(" ") || "Payment request failed.");
    };

    /**
     * Maps legacy/provider jargon (and known save failures) to plain-English
     * retry guidance. Charge declines already return friendly copy from the API.
     * Card/bank save failures during prepaid checkout are framed as payment failures.
     */
    toCustomerFacingPaymentError = (message: string): string => {
        const m = String(message || "").trim();
        if (/intuit payments card save conflicted|card may already exist/i.test(m)) {
            return "This card may already be on file. Please select it from your saved cards, or try a different card.";
        }
        if (/intuit payments card save failed|couldn't save your card/i.test(m)) {
            return "Payment could not be completed. Please check your card details and try again.";
        }
        if (/intuit payments bank save conflicted|bank may already exist/i.test(m)) {
            return "This bank account may already be on file. Please select it from your saved accounts, or try a different account.";
        }
        if (/intuit payments bank save failed|couldn't save your bank account/i.test(m)) {
            return "Payment could not be completed. Please check your account details and try again.";
        }
        if (/intuit payments charge failed/i.test(m)) {
            return "Payment failed. Please update your card information and try again.";
        }
        if (/intuit payments ach debit failed/i.test(m)) {
            return "Your bank payment failed. Please update your payment information and try again.";
        }
        if (/unexpected error while (saving card on file|saving bank account on file|creating intuit payments charge|charging saved card)/i.test(m)) {
            return "Payment failed. Please update your payment information and try again.";
        }
        return m || "Payment request failed.";
    };

    /**
     * ACH provider failures often expose raw QBO error bodies (`providerError`,
     * `providerBody`). Per backend contract those are diagnostic-only — log the
     * full payload and show a friendly message to the customer.
     */
    getAchPaymentErrorText = (error: ApiError | any): string => {
        const body = error?.body;
        const hasProviderDetail =
            (body && typeof body === "object" && ("providerError" in body || "providerBody" in body)) ||
            false;
        if (hasProviderDetail) {
            console.error("[ArtFeePay][ach] provider error", {
                status: error?.status,
                body,
            });
            return "Your bank payment failed. Please update your payment information and try again.";
        }
        return this.getPaymentErrorText(error);
    };

    /**
     * Best-effort device telemetry for Intuit risk scoring. All fields are
     * optional; we only forward what we know browser-side.
     */
    buildAchDevicePayload = (): { id?: string; type: string } => {
        let sessionId = "";
        try {
            sessionId = window.sessionStorage?.getItem("tgcc.payments.sessionId") || "";
            if (!sessionId && window.crypto && typeof window.crypto.getRandomValues === "function") {
                const bytes = new Uint8Array(8);
                window.crypto.getRandomValues(bytes);
                sessionId = "tgcc-" + Array.prototype.map.call(bytes, (b: number) => ("0" + b.toString(16)).slice(-2)).join("");
                window.sessionStorage?.setItem("tgcc.payments.sessionId", sessionId);
            }
        } catch {
            // sessionStorage / crypto can be blocked; the field is optional.
        }
        return sessionId ? { id: sessionId, type: "Web" } : { type: "Web" };
    };

    validatePayment = (): string | null => {
        const { paymentMode, paymentRail, achMode, selectedSavedCardId, selectedSavedBankAccountId, amount, customerId, cardInput, achInput } = this.state;
        const parsedAmount = Number(amount);
        if (!customerId) return "Customer ID is required.";
        if (!parsedAmount || parsedAmount <= 0) return "Amount must be greater than 0.";
        if (paymentRail === "card") {
            if (paymentMode === "saved" && !selectedSavedCardId) return "Select a saved card.";
            if (paymentMode === "new") {
                if (!cardInput.number || !/^\d{12,19}$/.test(cardInput.number.replace(/\s+/g, ""))) return "Card number is invalid.";
                if (!cardInput.cvc || !/^\d{3,4}$/.test(cardInput.cvc)) return "CVC must be 3 or 4 digits.";
                if (!cardInput.name.trim()) return "Cardholder name is required.";
                if (!cardInput.postalCode.trim()) return "Postal code is required.";
                const mm = Number(cardInput.expMonth);
                const yyyy = Number(cardInput.expYear);
                const now = new Date();
                const nowYear = now.getFullYear();
                const nowMonth = now.getMonth() + 1;
                if (mm < 1 || mm > 12) return "Expiration month must be between 1 and 12.";
                if (yyyy < nowYear) return "Expiration year cannot be in the past.";
                if (yyyy === nowYear && mm < nowMonth) {
                    return "This card has expired. The expiration month is before the current month.";
                }
            }
        } else {
            if (achMode === "saved") {
                if (!selectedSavedBankAccountId) return "Select a saved bank account.";
            } else {
                if (!achInput.accountHolderName.trim()) return "Account holder name is required.";
                if (!/^\d{9}$/.test(achInput.routingNumber)) return "Routing number must be 9 digits.";
                if (!/^\d{4,17}$/.test(achInput.accountNumber)) return "Account number is invalid.";
            }
        }
        const limitError = this.validateChargeAmountWithinLimit(this.getTotalDue());
        if (limitError) return limitError;
        return null;
    };

    runCharge = async () => {
        const webOrderAuth = this.getWebOrderAuth();
        if (!webOrderAuth) {
            this.setState({
                paymentError: "Missing payment link token. No backend calls were made.",
            });
            return;
        }

        if (!this.isLinkPayable()) {
            this.setState({ paymentError: this.getNotPayableMessage() });
            return;
        }

        const validationMessage = this.validatePayment();
        if (validationMessage) {
            this.setState({ paymentError: validationMessage });
            return;
        }

        const {
            paymentMode,
            paymentRail,
            achMode,
            cardInput,
            saveCardForFuture,
            saveBankForFuture,
            customerId,
            currency,
            description,
            selectedSavedCardId,
            savedCards,
            achInput,
            savedBankAccounts,
        } = this.state;

        if (paymentRail === "ach") {
            const totalChargeAmount = Number(this.getParsedAmount().toFixed(2));
            const achLimitError = this.validateChargeAmountWithinLimit(totalChargeAmount);
            if (achLimitError) {
                this.setState({ paymentError: achLimitError });
                return;
            }
            this.setState({ isPaymentProcessing: true, processingStep: "Preparing ACH payment...", paymentError: null, chargeResult: null, paymentId: null });
            try {
                let achCharge: ChargeResult;

                if (achMode === "saved") {
                    const bankIdToCharge = this.state.selectedSavedBankAccountId;
                    this.setState({ processingStep: "Charging bank account..." });
                    achCharge = await chargeSavedBank({
                        bankId: bankIdToCharge,
                        amount: totalChargeAmount,
                        currency,
                        description,
                        customerId,
                        device: this.buildAchDevicePayload(),
                    }, webOrderAuth);
                } else {
                    this.setState({ processingStep: "Tokenizing bank account..." });
                    const bankPayload = {
                        name: achInput.accountHolderName,
                        routingNumber: achInput.routingNumber,
                        accountNumber: achInput.accountNumber,
                        accountType: achInput.accountType === "savings" ? "PERSONAL_SAVINGS" : "PERSONAL_CHECKING",
                    };
                    // Tokens are single-use and short-lived; mint immediately before submit.
                    const paymentsToken = await tokenizeBankInBrowser(bankPayload);
                    if (!paymentsToken) {
                        throw new Error("Bank tokenization completed but no token was returned.");
                    }

                    if (saveBankForFuture) {
                        // Mirror the card flow: save the bank first to get a
                        // 24-digit Intuit on-file id, then charge against it
                        // via /intuit-charge-saved-bank.
                        this.setState({ processingStep: "Saving bank account..." });
                        const savedBank = await saveBankOnFile(
                            {
                                paymentsToken,
                                accountHolderName: achInput.accountHolderName,
                                customerId,
                                accountTypeHint: achInput.accountType,
                                last4Hint: achInput.accountNumber.slice(-4),
                            },
                            webOrderAuth
                        );

                        const updatedAccounts = [savedBank].concat(
                            savedBankAccounts.filter((a) => a.bankAccountId !== savedBank.bankAccountId)
                        );
                        this.saveSavedBankAccounts(updatedAccounts, this.getWebOrderTokenValue());
                        this.setState({
                            savedBankAccounts: updatedAccounts,
                            selectedSavedBankAccountId: savedBank.bankAccountId,
                        });

                        this.setState({ processingStep: "Charging saved bank account..." });
                        achCharge = await chargeSavedBank(
                            {
                                bankId: savedBank.bankAccountId,
                                amount: totalChargeAmount,
                                currency,
                                description,
                                customerId,
                                device: this.buildAchDevicePayload(),
                            },
                            webOrderAuth
                        );
                    } else {
                        // No save requested — one-time ACH only.
                        this.setState({ processingStep: "Submitting bank payment..." });
                        achCharge = await chargeOneTimeBank(
                            {
                                paymentsToken,
                                amount: totalChargeAmount,
                                currency,
                                description,
                                customerId,
                                accountHolderName: achInput.accountHolderName,
                                device: this.buildAchDevicePayload(),
                            },
                            webOrderAuth
                        );
                    }
                }

                this.finalizeSuccessfulPayment(achCharge, {
                    achInput: {
                        ...this.state.achInput,
                        routingNumber: "",
                        accountNumber: "",
                    },
                });
            } catch (err) {
                const friendly = this.getAchPaymentErrorText(err);
                this.setState({
                    isPaymentProcessing: false,
                    processingStep: null,
                    paymentError: friendly,
                });
            }
            return;
        }

        this.setState({ isPaymentProcessing: true, processingStep: "Preparing payment...", paymentError: null, chargeResult: null, paymentId: null });

        try {
            let currentCardId = selectedSavedCardId;
            let charge: ChargeResult;

            if (paymentMode === "saved") {
                this.setState({ processingStep: "Charging..." });
                const selectedSaved = savedCards.find((c) => c.cardId === selectedSavedCardId);
                const savedFunding = selectedSaved?.cardFundingType
                    ? String(selectedSaved.cardFundingType).trim()
                    : "";
                const finalCardFunding = normalizeStoredCardFunding(savedFunding);
                const totalChargeAmount = Number(this.getCardTotalDueForFunding(finalCardFunding).toFixed(2));
                const cardLimitError = this.validateChargeAmountWithinLimit(totalChargeAmount);
                if (cardLimitError) {
                    this.setState({ isPaymentProcessing: false, processingStep: null, paymentError: cardLimitError });
                    return;
                }
                charge = await chargeSavedCard(
                    {
                        cardId: selectedSavedCardId,
                        amount: totalChargeAmount,
                        currency,
                        description,
                        customerId,
                        ...(savedFunding ? { cardFundingType: savedFunding } : {}),
                    },
                    webOrderAuth
                );
            } else {
                const cardPayload = {
                    ...cardInput,
                    number: cardInput.number.replace(/\s+/g, ""),
                };
                this.setState({ processingStep: "Looking up card type..." });
                const cardFundingType = await resolveCardFundingTypeFromCardNumber(cardPayload.number);
                const finalCardFunding = cardFundingType || "unknown";
                const totalChargeAmount = Number(this.getCardTotalDueForFunding(finalCardFunding).toFixed(2));
                const cardLimitError = this.validateChargeAmountWithinLimit(totalChargeAmount);
                if (cardLimitError) {
                    this.setState({ isPaymentProcessing: false, processingStep: null, paymentError: cardLimitError });
                    return;
                }
                this.setState({ processingStep: "Tokenizing card..." });
                const paymentsToken = await tokenizeCardInBrowser(cardPayload);
                const tokenPreview = this.getTokenPreview(paymentsToken);
                this.logCardSaveFlow("tokenization success", {
                    saveCardRequested: saveCardForFuture,
                    tokenPreview,
                    customerId,
                    webOrderNumber: webOrderAuth?.webOrderNumber,
                });
                if (!paymentsToken) {
                    throw new Error("Tokenization completed but no token was returned.");
                }

                if (saveCardForFuture) {
                    this.setState({ processingStep: "Saving card..." });
                    this.logCardSaveFlow("save-card requested", {
                        saveCardRequested: true,
                        tokenPreview,
                        customerId,
                        webOrderNumber: webOrderAuth?.webOrderNumber,
                    });

                    let savedCard: SavedCard;
                    try {
                        savedCard = await saveCardOnFile(
                            {
                                customerId,
                                cardholderName: cardInput.name,
                                paymentsToken,
                                ...(cardFundingType ? { cardFundingType } : {}),
                            },
                            webOrderAuth
                        );
                        this.logCardSaveFlow("backend save-card response", {
                            status: "success",
                            tokenPreview,
                            customerId,
                            savedCardId: savedCard.cardId,
                        });
                    } catch (saveErr) {
                        const apiErr = saveErr as ApiError;
                        this.logCardSaveFlow("backend save-card response", {
                            status: apiErr?.status || "error",
                            tokenPreview,
                            customerId,
                            providerMessage: apiErr?.body?.message || apiErr?.message,
                        });
                        throw saveErr;
                    }

                    currentCardId = savedCard.cardId;
                    const updated = [savedCard].concat(savedCards.filter((c) => c.cardId !== savedCard.cardId));
                    this.saveSavedCards(updated, this.getWebOrderTokenValue());
                    this.setState({ savedCards: updated, selectedSavedCardId: savedCard.cardId, cardId: savedCard.cardId });

                    this.setState({ processingStep: "Charging..." });
                    charge = await chargeSavedCard(
                        {
                            cardId: savedCard.cardId,
                            amount: totalChargeAmount,
                            currency,
                            description,
                            customerId,
                            ...(cardFundingType ? { cardFundingType } : {}),
                        },
                        webOrderAuth
                    );
                } else {
                    this.logCardSaveFlow("save-card requested", {
                        saveCardRequested: false,
                        tokenPreview,
                        customerId,
                        webOrderNumber: webOrderAuth?.webOrderNumber,
                    });

                    this.setState({ processingStep: "Charging..." });
                    charge = await chargeOneTimeToken(
                        {
                            token: paymentsToken,
                            amount: totalChargeAmount,
                            currency,
                            description,
                            customerId,
                            ...(cardFundingType ? { cardFundingType } : {}),
                        },
                        webOrderAuth
                    );
                }
            }

            this.finalizeSuccessfulPayment(charge, {
                cardId: currentCardId || null,
                cardInput: {
                    ...this.state.cardInput,
                    number: "",
                    cvc: "",
                },
            });
        } catch (err) {
            const errMessage = String((err as any)?.message || "");
            this.setState({
                isPaymentProcessing: false,
                processingStep: null,
                paymentError:
                    /tokenization provider is not available|Raw payment data is blocked/i.test(errMessage)
                        ? this.getRawDataBlockedMessage()
                        : this.getPaymentErrorText(err),
            });
        }
    };

    closeErrorPopup = () => {
        this.setState({ errorMessage: null });
    };

    getErrorHeading = (errorMessage: string | null): string => {
        if (!errorMessage) {
            return "Something went wrong";
        }
        if (/invalid or expired payment link/i.test(errorMessage)) {
            return "Payment link unavailable";
        }
        return "Error";
    };

    closeConfirmationPopup = () => {
        this.setState({ showConfirmation: false });
        browserHistory.push(Tgcc.Routes.DashBoard);
    };

    getConfirmationMessage = (): string => {
        const charge = this.state.chargeResult;
        const isAchPending = !!charge && (charge.paymentRail === "ach" || charge.isPending);
        if (isAchPending) {
            return (
                "Your art design fee payment was submitted successfully. " +
                "ACH transfers typically settle in 1–3 business days; we'll email you when settlement completes."
            );
        }
        return "Your art design fee payment was received successfully! You'll receive a confirmation email shortly.";
    };
    
    render() {
        const { webOrderNumber, customerName, customerFirstName, isLoading, errorMessage, showConfirmation } = this.state;
        const greetingName = customerFirstName || customerName;
        const linkPayable = this.isLinkPayable();
        const notPayableMessage = linkPayable ? null : this.getNotPayableMessage();

        return (
            <div style={styles.pageContainer}>
                {errorMessage && (
                    <EstimateErrorPopup 
                        message={errorMessage} 
                        heading={this.getErrorHeading(errorMessage)} 
                        onClose={this.closeErrorPopup} 
                    />
                )}
                {showConfirmation && (
                    <EstimateConfirmationPopup 
                        message={this.getConfirmationMessage()}
                        heading={"Payment Received"}
                        onClose={this.closeConfirmationPopup}
                    />
                )}
                
                <MajorAccountsHdr />
                
                <div
                    style={{
                        ...styles.contentContainer,
                        ...styles.contentContainerCheckout,
                    }}
                >
                    <div style={styles.header}>
                        <div style={styles.titleSection}>
                            <div style={styles.iconContainer}>
                                <span style={styles.icon}>🎨</span>
                            </div>
                            <div>
                                <h1 style={styles.title}>Art Design Fee</h1>
                                <p style={styles.subtitle}>
                                    {!isLoading && webOrderNumber && !linkPayable
                                        ? notPayableMessage
                                        : greetingName
                                        ? `Hi ${greetingName}, complete your art design fee payment below.`
                                        : "Complete your art design fee payment below."}
                                </p>
                            </div>
                        </div>
                        
                        {!isLoading && webOrderNumber && (
                            <div style={styles.orderBadge}>
                                <span style={styles.badgeIcon}>📦</span>
                                Order {webOrderNumber}
                            </div>
                        )}
                    </div>

                    {isLoading ? (
                        <div style={styles.loadingContainer}>
                            <div style={styles.spinner}></div>
                            <p style={styles.loadingText}>Loading payment details...</p>
                        </div>
                    ) : webOrderNumber && !linkPayable ? (
                        <div style={styles.card}>
                            <div style={styles.confirmationSection}>
                                <div style={styles.checkmarkContainer}>
                                    <div style={styles.checkmarkCircle}>
                                        <span style={styles.checkmark}>✓</span>
                                    </div>
                                </div>
                                <div style={styles.confirmationContent}>
                                    <h3 style={styles.confirmationTitle}>Payment unavailable</h3>
                                    <p style={styles.confirmationText}>{notPayableMessage}</p>
                                    <div style={styles.featureList}>
                                        <div style={styles.featureItem}>
                                            <span style={styles.featureIcon}>📦</span>
                                            <span>Order {webOrderNumber}</span>
                                        </div>
                                        {this.getParsedAmount() > 0 ? (
                                            <div style={styles.featureItem}>
                                                <span style={styles.featureIcon}>💳</span>
                                                <span>Art design fee: ${this.getParsedAmount().toFixed(2)}</span>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : webOrderNumber ? (
                        <>
                            <div style={styles.card}>
                                <div style={styles.confirmationSection}>
                                    <div style={styles.checkmarkContainer}>
                                        <div style={styles.checkmarkCircle}>
                                            <span style={styles.checkmark}>$</span>
                                        </div>
                                    </div>
                                    <div style={styles.confirmationContent}>
                                        <h3 style={styles.confirmationTitle}>Art Design Fee</h3>
                                        <p style={styles.confirmationText}>
                                            {greetingName ? (
                                                <>
                                                    Hi <strong>{greetingName}</strong>, please pay the art design fee for{" "}
                                                    <strong>Order {webOrderNumber}</strong>.
                                                </>
                                            ) : (
                                                <>
                                                    Please pay the art design fee for <strong>Order {webOrderNumber}</strong>.
                                                </>
                                            )}
                                        </p>
                                        <div style={styles.featureList}>
                                            <div style={styles.featureItem}>
                                                <span style={styles.featureIcon}>💳</span>
                                                <span>Art design fee: ${this.getParsedAmount().toFixed(2)}</span>
                                            </div>
                                            <div style={styles.featureItem}>
                                                <span style={styles.featureIcon}>🔒</span>
                                                <span>Secure payment processing</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={styles.paymentShell}>
                                <div style={styles.checkoutHeaderBar}>
                                    <span style={styles.checkoutBackText}>Checkout</span>
                                    <span style={styles.checkoutBrand}>TGCC Pay</span>
                                </div>
                                <div style={styles.paymentHero}>
                                    <div>
                                        <h3 style={styles.paymentHeroTitle}>Payment Details</h3>
                                        <p style={styles.paymentHeroSubtitle}>
                                    Debit cards have no card processing fee; credit and prepaid use a flat 3%. Bank transfer (ACH) is always fee-free.
                                </p>
                                    </div>
                                </div>
                                <div style={styles.progressSteps}>
                                    <span style={{ ...styles.progressStep, ...styles.progressStepDone }}>1 Information</span>
                                    <span style={{ ...styles.progressStep, ...styles.progressStepActive }}>2 Payment Details</span>
                                    <span style={styles.progressStep}>3 Complete Order</span>
                                </div>
                                <div className="estimate-payment-layout" style={styles.paymentLayout}>
                                    <div style={styles.paymentLeft}>
                                        <div style={styles.paymentFormCard}>
                                            <FeeComparisonPanel paymentRail={this.state.paymentRail} onSelectRail={this.setPaymentRail} />
                                            {this.state.paymentRail === "card" ? (
                                                <PaymentMethodSelector
                                                    paymentMode={this.state.paymentMode}
                                                    onSelectMode={this.setPaymentMode}
                                                />
                                            ) : null}
                                            {this.state.paymentRail === "card" && this.state.paymentMode === "new" ? (
                                                <CardForm
                                                    cardInput={this.state.cardInput}
                                                    saveCardForFuture={this.state.saveCardForFuture}
                                                    onInputChange={this.onCardInputChange}
                                                    onToggleSave={(checked) => this.setState({ saveCardForFuture: checked })}
                                                    showFundingFeeHint
                                                />
                                            ) : null}
                                            {this.state.paymentRail === "card" && this.state.paymentMode === "saved" ? (
                                                <SavedCardList
                                                    cards={this.state.savedCards}
                                                    selectedCardId={this.state.selectedSavedCardId}
                                                    onSelect={(cardId) => this.setState({ selectedSavedCardId: cardId })}
                                                />
                                            ) : null}
                                            {this.state.paymentRail === "ach" ? (
                                                <>
                                                    <AchModeSelector
                                                        achMode={this.state.achMode}
                                                        onSelectMode={this.setAchMode}
                                                    />
                                                    {this.state.achMode === "new" ? (
                                                        <AchForm
                                                            achInput={this.state.achInput}
                                                            saveBankForFuture={this.state.saveBankForFuture}
                                                            onToggleSaveBank={(checked) => this.setState({ saveBankForFuture: checked })}
                                                            onInputChange={this.onAchInputChange}
                                                        />
                                                    ) : (
                                                        <SavedBankAccountList
                                                            accounts={this.state.savedBankAccounts}
                                                            selectedBankAccountId={this.state.selectedSavedBankAccountId}
                                                            onSelect={(bankAccountId) => this.setState({ selectedSavedBankAccountId: bankAccountId })}
                                                        />
                                                    )}
                                                </>
                                            ) : null}
                                            {this.state.paymentRail === "card" ? (
                                                <CardFundingInsight
                                                    styles={styles}
                                                    paymentMode={this.state.paymentMode}
                                                    binPreviewPhase={this.state.binPreviewPhase}
                                                    effectiveFunding={this.getEffectiveFundingForFee()}
                                                    subtotal={this.getParsedAmount()}
                                                />
                                            ) : null}
                                            {this.state.paymentRail === "ach" ? (
                                                <AchAuthorizationNotice amount={this.getTotalDue()} />
                                            ) : null}
                                            {this.state.paymentError ? (
                                                <div
                                                    ref={this.paymentErrorBannerRef}
                                                    role="alert"
                                                    aria-live="assertive"
                                                    style={styles.paymentErrorBanner}
                                                >
                                                    <div style={styles.paymentErrorBannerTitle}>Payment unsuccessful</div>
                                                    <div style={styles.paymentErrorBannerBody}>{this.state.paymentError}</div>
                                                </div>
                                            ) : null}
                                            <button
                                                style={{
                                                    ...styles.paymentSubmitButton,
                                                    ...(this.state.isPaymentProcessing ? styles.buttonDisabled : {}),
                                                }}
                                                onClick={this.runCharge}
                                                disabled={this.state.isPaymentProcessing}
                                            >
                                                {this.state.isPaymentProcessing
                                                    ? "Processing..."
                                                    : `Pay $${this.getTotalDue().toFixed(2)}`}
                                            </button>
                                            {this.state.processingStep && <div style={styles.helperText}>Status: {this.state.processingStep}</div>}
                                            <ChargeResultPanel
                                                result={this.state.chargeResult}
                                            />
                                        </div>
                                    </div>
                                    <div style={styles.paymentRight}>
                                        <div className="estimate-payment-summary" style={styles.summaryCard}>
                                            <h4 style={styles.summaryTitle}>Order summary</h4>
                                            <div style={styles.summarySubLabel}>Total you&apos;ll pay</div>
                                            <div style={styles.summaryTotal}>${this.getTotalDue().toFixed(2)}</div>
                                            <p style={{ margin: "0.35rem 0 0 0", fontSize: "0.84rem", color: "#64748b", lineHeight: 1.45 }}>
                                                {this.state.paymentRail === "ach"
                                                    ? "Bank transfer — no processing fee."
                                                    : this.getProcessingFee() <= 0
                                                    ? "Debit card — no card processing fee."
                                                    : "Includes card processing (flat 3%)."}
                                            </p>
                                            <div style={styles.summaryDivider}></div>
                                            <div style={styles.summaryRow}>
                                                <span>Art design fee</span>
                                                <span style={{ fontWeight: 700 }}>${this.getParsedAmount().toFixed(2)}</span>
                                            </div>
                                            <div style={styles.summaryRow}>
                                                <span>Card processing</span>
                                                <span style={{ fontWeight: 700 }}>
                                                    {this.state.paymentRail === "ach"
                                                        ? "$0.00"
                                                        : `$${this.getProcessingFee().toFixed(2)}`}
                                                </span>
                                            </div>
                                            {this.state.paymentRail === "card" ? (
                                                <div style={styles.summaryFeeDetail}>
                                                    {this.getEffectiveFundingForFee() === "Debit"
                                                        ? "No fee for debit cards."
                                                        : this.getEffectiveFundingForFee() === "unknown"
                                                        ? "Assumes credit/prepaid until we confirm your card at payment."
                                                        : "Applies to credit and prepaid cards (flat 3%)."}
                                                </div>
                                            ) : (
                                                <div style={styles.summaryFeeDetail}>ACH payments have no TGCC processing fee.</div>
                                            )}
                                            <div style={styles.summaryDivider}></div>
                                            <div style={styles.summaryRow}>
                                                <span>Order</span>
                                                <span>{webOrderNumber}</span>
                                            </div>
                                            <div style={styles.summaryRow}>
                                                <span>Currency</span>
                                                <span>{this.state.currency}</span>
                                            </div>
                                            <div style={styles.summaryRow}>
                                                <span>Paying with</span>
                                                <span style={{ fontWeight: 700, textAlign: "right" }}>
                                                    {this.state.paymentRail === "ach"
                                                        ? "eCheck (ACH)"
                                                        : this.state.paymentMode === "saved"
                                                        ? "Saved card"
                                                        : "New card"}
                                                </span>
                                            </div>
                                            {this.state.paymentRail === "card" ? (
                                                <div style={styles.summaryFundingStrip}>
                                                    <div style={styles.summaryFundingStripTitle}>Card type (for fees)</div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                                        <span style={fundingBadgeStyle(this.getEffectiveFundingForFee())}>
                                                            {fundingLabel(this.getEffectiveFundingForFee())}
                                                        </span>
                                                        <span style={{ fontSize: "0.78rem", color: "#64748b" }}>
                                                            {this.state.paymentMode === "new" && this.state.binPreviewPhase === "loading"
                                                                ? "Verifying…"
                                                                : this.state.paymentMode === "new" && this.state.binPreviewPhase === "short"
                                                                ? "Enter more digits to detect"
                                                                : null}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : null}
                                            <div style={styles.summaryDivider}></div>
                                            <div style={styles.summarySecurity}>
                                                🔒 Payments are encrypted and processed securely.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        );
    }
}

const styles: { [key: string]: React.CSSProperties } = {
    pageContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        background: "#f5f7fb",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
        padding: "20px",
    },
    contentContainer: {
        width: "100%",
        maxWidth: "800px",
        marginTop: "100px",
        animation: "slideUp 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
    },
    contentContainerCheckout: {
        maxWidth: "1040px",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "2rem",
        gap: "2rem",
    },
    titleSection: {
        display: "flex",
        alignItems: "flex-start",
        gap: "1rem",
        flex: 1,
    },
    iconContainer: {
        background: "#eaf2ff",
        borderRadius: "14px",
        padding: "10px 12px",
        fontSize: "1.25rem",
        border: "1px solid #bfdbfe",
    },
    icon: {
        filter: "none",
    },
    title: {
        color: "#0f172a",
        fontSize: "2.25rem",
        fontWeight: 800,
        letterSpacing: "-0.02em",
        margin: "0 0 0.5rem 0",
        lineHeight: 1.2,
    },
    subtitle: {
        color: "#6b6b8a",
        fontSize: "1.1rem",
        lineHeight: 1.5,
        margin: 0,
        opacity: 0.9,
    },
    orderBadge: {
        background: "#eef2ff",
        color: "#1d4ed8",
        padding: "0.65rem 1rem",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "0.88rem",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        border: "1px solid #c7d2fe",
    },
    badgeIcon: {
        fontSize: "1.1rem",
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "4rem 2rem",
        textAlign: "center",
    },
    spinner: {
        width: "48px",
        height: "48px",
        border: "4px solid #f3f0ff",
        borderLeft: "4px solid #28a745",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        marginBottom: "1rem",
    },
    loadingText: {
        color: "#6b6b8a",
        fontSize: "1.1rem",
        margin: 0,
    },
    card: {
        background: "#ffffff",
        borderRadius: "16px",
        padding: "1.25rem",
        marginBottom: "2rem",
        boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
        border: "1px solid #e5e7eb",
    },
    confirmationSection: {
        display: "flex",
        alignItems: "flex-start",
        gap: "1.5rem",
    },
    checkmarkContainer: {
        flexShrink: 0,
    },
    checkmarkCircle: {
        width: "54px",
        height: "54px",
        background: "#eff6ff",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.45rem",
        color: "#1d4ed8",
        boxShadow: "none",
        border: "1px solid #bfdbfe",
    },
    checkmark: {
        fontWeight: "bold",
    },
    confirmationContent: {
        flex: 1,
    },
    confirmationTitle: {
        color: "#0f172a",
        fontSize: "1.25rem",
        fontWeight: 800,
        margin: "0 0 0.65rem 0",
    },
    confirmationText: {
        color: "#4b5563",
        lineHeight: 1.6,
        margin: "0 0 0.95rem 0",
    },
    featureList: {
        display: "flex",
        flexDirection: "column",
        gap: "0.55rem",
    },
    featureItem: {
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        color: "#475569",
        fontSize: "0.86rem",
    },
    featureIcon: {
        fontSize: "1.1rem",
    },
    actionSection: {
        textAlign: "center",
        marginBottom: "2rem",
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "16px",
        padding: "1rem",
        boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
    },
    buttonGroup: {
        display: "flex",
        gap: "1rem",
        justifyContent: "center",
        marginBottom: "1.5rem",
        flexWrap: "wrap",
    },
    approveButton: {
        background: "#2563eb",
        color: "white",
        padding: "0.85rem 1.6rem",
        fontSize: "0.98rem",
        borderRadius: "12px",
        border: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.55rem",
        transition: "all 0.3s ease",
        boxShadow: "0 10px 22px rgba(37, 99, 235, 0.28)",
        fontWeight: 700,
        minWidth: "190px",
        justifyContent: "center",
    },
    buttonDisabled: {
        opacity: 0.7,
        cursor: "not-allowed",
        transform: "none !important",
    },
    requestButton: {
        background: "#ffffff",
        color: "#334155",
        padding: "0.85rem 1.45rem",
        fontSize: "0.98rem",
        borderRadius: "12px",
        border: "1px solid #cbd5e1",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.55rem",
        transition: "all 0.3s ease",
        boxShadow: "0 8px 16px rgba(15, 23, 42, 0.07)",
        fontWeight: 700,
        minWidth: "180px",
        justifyContent: "center",
    },
    buttonIcon: {
        fontSize: "1.05rem",
    },
    buttonSpinner: {
        width: "18px",
        height: "18px",
        border: "2px solid transparent",
        borderLeft: "2px solid white",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
    },
    helperText: {
        color: "#64748b",
        fontSize: "0.82rem",
        fontWeight: 500,
        marginTop: "0.6rem",
    },
    errorText: {
        color: "#b00020",
        marginTop: "0.75rem",
        fontWeight: 600,
        fontSize: "0.9rem",
    },
    paymentErrorBanner: {
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderLeft: "4px solid #b00020",
        borderRadius: "12px",
        padding: "0.95rem 1.05rem",
        margin: "0.85rem 0 0.95rem 0",
        color: "#7f1d1d",
        boxShadow: "0 6px 16px rgba(127, 29, 29, 0.08)",
    },
    paymentErrorBannerTitle: {
        fontSize: "1.08rem",
        fontWeight: 800,
        letterSpacing: "0.01em",
        marginBottom: "0.3rem",
        color: "#7f1d1d",
    },
    paymentErrorBannerBody: {
        fontSize: "0.98rem",
        fontWeight: 600,
        lineHeight: 1.45,
        color: "#991b1b",
    },
    paymentShell: {
        marginBottom: "2rem",
        background: "#f8fafc",
        border: "1px solid #e5e7eb",
        borderRadius: "18px",
        padding: "1.1rem",
    },
    checkoutHeaderBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "0.9rem",
        fontSize: "0.82rem",
        color: "#6b7280",
    },
    checkoutBackText: {
        fontWeight: 600,
    },
    checkoutBrand: {
        fontWeight: 800,
        color: "#111827",
        letterSpacing: "0.02em",
    },
    paymentHero: {
        background: "#ffffff",
        borderRadius: "14px",
        padding: "1.15rem 1.25rem",
        color: "#111827",
        marginBottom: "0.9rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "0.8rem",
        border: "1px solid #e5e7eb",
    },
    paymentHeroTitle: {
        margin: 0,
        fontSize: "1.35rem",
        fontWeight: 800,
        letterSpacing: "0.01em",
    },
    paymentHeroSubtitle: {
        margin: "0.35rem 0 0 0",
        color: "#6b7280",
        fontSize: "1rem",
    },
    progressSteps: {
        display: "flex",
        alignItems: "center",
        gap: "0.55rem",
        marginBottom: "0.9rem",
        flexWrap: "wrap",
    },
    progressStep: {
        fontSize: "0.79rem",
        color: "#6b7280",
        border: "1px dashed #d1d5db",
        borderRadius: "999px",
        padding: "0.25rem 0.65rem",
        fontWeight: 600,
    },
    progressStepDone: {
        color: "#166534",
        border: "1px solid #86efac",
        background: "#f0fdf4",
    },
    progressStepActive: {
        color: "#1d4ed8",
        border: "1px solid #93c5fd",
        background: "#eff6ff",
    },
    paymentFormCard: {
        background: "#ffffff",
        borderRadius: "16px",
        padding: "1rem 1rem 1.1rem 1rem",
        border: "1px solid #e5e7eb",
        boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
    },
    paymentLayout: {
        display: "grid",
        gridTemplateColumns: "1.25fr 0.75fr",
        gap: "1rem",
        alignItems: "start",
    },
    paymentLeft: {
        minWidth: 0,
    },
    paymentRight: {
        minWidth: 0,
    },
    summaryCard: {
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "16px",
        padding: "1rem",
        boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
        position: "sticky",
        top: "110px",
    },
    summaryTitle: {
        margin: 0,
        color: "#0f172a",
        fontSize: "1.1rem",
        fontWeight: 800,
    },
    summaryTotal: {
        marginTop: "0.6rem",
        color: "#1d4ed8",
        fontSize: "2.2rem",
        fontWeight: 800,
        letterSpacing: "-0.02em",
    },
    summaryDivider: {
        height: "1px",
        background: "#e5e7eb",
        margin: "0.85rem 0",
    },
    summaryRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: "0.75rem",
        color: "#374151",
        fontSize: "0.9rem",
        marginBottom: "0.45rem",
    },
    summarySecurity: {
        color: "#4b5563",
        fontSize: "0.82rem",
        lineHeight: 1.45,
    },
    summarySubLabel: {
        fontSize: "0.72rem",
        fontWeight: 600,
        color: "#64748b",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: "0.2rem",
    },
    summaryFeeDetail: {
        fontSize: "0.78rem",
        color: "#64748b",
        lineHeight: 1.4,
        marginTop: "0.25rem",
    },
    summaryFundingStrip: {
        marginTop: "0.65rem",
        padding: "0.65rem 0.75rem",
        borderRadius: "12px",
        background: "#ffffff",
        border: "1px solid #e2e8f0",
    },
    summaryFundingStripTitle: {
        fontSize: "0.72rem",
        fontWeight: 800,
        color: "#0f172a",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: "0.35rem",
    },
    fundingCallout: {
        borderRadius: "16px",
        padding: "0.9rem 1rem",
        marginBottom: "0.85rem",
        background: "linear-gradient(135deg, rgba(99, 102, 241, 0.07) 0%, rgba(14, 165, 233, 0.07) 100%)",
        border: "1px solid rgba(99, 102, 241, 0.22)",
    },
    fundingCalloutTitle: {
        fontWeight: 800,
        fontSize: "0.8rem",
        color: "#0f172a",
        marginBottom: "0.3rem",
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
    },
    fundingCalloutBody: {
        fontSize: "0.83rem",
        color: "#334155",
        lineHeight: 1.5,
    },
    feeOptionSub: {
        fontSize: "0.76rem",
        color: "#64748b",
        lineHeight: 1.4,
        marginTop: "0.25rem",
    },
    feeOptionHighlight: {
        fontSize: "0.88rem",
        color: "#059669",
        fontWeight: 800,
        marginTop: "0.15rem",
    },
    cardHintMicro: {
        fontSize: "0.9rem",
        color: "#64748b",
        marginTop: "-0.25rem",
        marginBottom: "0.6rem",
        lineHeight: 1.5,
    },
    savedCardBadgeRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.35rem",
        marginTop: "0.45rem",
    },
    paymentMethodTabs: {
        display: "flex",
        background: "#f3f4f6",
        borderRadius: "14px",
        padding: "6px",
        gap: "8px",
        marginBottom: "1rem",
        border: "1px solid #e2e8f0",
    },
    paymentTab: {
        flex: 1,
        border: "none",
        padding: "0.85rem 1rem",
        borderRadius: "10px",
        fontWeight: 700,
        cursor: "pointer",
        background: "transparent",
        color: "#293056",
        transition: "all 0.2s ease",
        fontSize: "1.02rem",
    },
    paymentTabActive: {
        background: "#111827",
        color: "white",
        boxShadow: "0 8px 16px rgba(17, 24, 39, 0.25)",
    },
    fieldGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "1rem",
        marginBottom: "1rem",
    },
    inputSectionCard: {
        border: "1px solid #e5e7eb",
        borderRadius: "14px",
        padding: "1.15rem 1.15rem 1.2rem 1.15rem",
        background: "#ffffff",
        marginBottom: "0.8rem",
    },
    feeComparisonCard: {
        border: "1px solid #dbeafe",
        borderRadius: "14px",
        padding: "0.9rem",
        background: "#f8fbff",
        marginBottom: "0.8rem",
    },
    feeComparisonGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "0.65rem",
    },
    feeOptionCard: {
        border: "1px solid #cbd5e1",
        borderRadius: "12px",
        background: "#ffffff",
        padding: "0.7rem 0.75rem",
        textAlign: "left",
        cursor: "pointer",
    },
    feeOptionCardActive: {
        border: "1px solid #1d4ed8",
        boxShadow: "0 8px 16px rgba(29, 78, 216, 0.18)",
        background: "#eff6ff",
    },
    feeOptionTitle: {
        color: "#0f172a",
        fontWeight: 800,
        fontSize: "0.85rem",
        marginBottom: "0.2rem",
    },
    feeOptionValue: {
        color: "#1d4ed8",
        fontWeight: 800,
        fontSize: "0.92rem",
        marginBottom: "0.2rem",
    },
    feeOptionHint: {
        color: "#64748b",
        fontSize: "0.78rem",
    },
    sectionTitle: {
        margin: "0 0 0.35rem 0",
        color: "#111827",
        fontSize: "1.1rem",
        fontWeight: 800,
        letterSpacing: "0.01em",
    },
    inputWrap: {
        display: "block",
    },
    input: {
        width: "100%",
        padding: "0.95rem 1rem",
        borderRadius: "10px",
        border: "1px solid #d1d5db",
        background: "#ffffff",
        fontSize: "1.05rem",
        color: "#1f2340",
        boxSizing: "border-box",
        outline: "none",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    },
    label: {
        display: "block",
        color: "#374151",
        fontSize: "0.95rem",
        marginBottom: "0.4rem",
        fontWeight: 600,
        letterSpacing: "0.01em",
    },
    checkboxRow: {
        display: "flex",
        alignItems: "center",
        gap: "0.55rem",
        margin: "0.4rem 0 1rem 0",
        color: "#4d4f69",
        fontSize: "1rem",
        fontWeight: 600,
    },
    savedCardRow: {
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "0.8rem 0.85rem",
        marginBottom: "0.65rem",
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        gap: "0.65rem",
    },
    savedCardRowActive: {
        border: "1px solid #1d4ed8",
        background: "#eff6ff",
        boxShadow: "0 8px 16px rgba(59, 130, 246, 0.2)",
    },
    savedCardLabelWrap: {
        display: "flex",
        flexDirection: "column",
        gap: "0.18rem",
    },
    savedCardMeta: {
        color: "#6b7280",
        fontSize: "0.78rem",
    },
    savedCardTile: {
        width: "100%",
        border: "1px solid #e5e7eb",
        borderRadius: "14px",
        background: "#ffffff",
        padding: "0.85rem 0.9rem",
        marginBottom: "0.65rem",
        cursor: "pointer",
        textAlign: "left",
        boxShadow: "0 10px 18px rgba(15, 23, 42, 0.04)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
    },
    savedCardTileActive: {
        border: "1px solid #1d4ed8",
        background: "#eff6ff",
        boxShadow: "0 14px 26px rgba(29, 78, 216, 0.12)",
    },
    savedCardTileTop: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "0.6rem",
        gap: "0.8rem",
    },
    savedCardBrand: {
        width: "42px",
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#111827",
        fontWeight: 900,
    },
    savedCardLast4: {
        color: "#0f172a",
        fontWeight: 800,
        letterSpacing: "0.02em",
        opacity: 0.95,
    },
    savedCardTileBottom: {
        display: "flex",
        flexDirection: "column",
        gap: "0.25rem",
    },
    savedCardName: {
        color: "#1f2937",
        fontWeight: 800,
        fontSize: "0.95rem",
    },
    // Modern Saved Cards Styles
    savedCardsContainer: {
        marginBottom: "1rem",
    },
    savedCardsHeader: {
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        marginBottom: "1rem",
        padding: "0 0.25rem",
    },
    savedCardsHeaderIcon: {
        fontSize: "1.25rem",
    },
    savedCardsHeaderTitle: {
        margin: 0,
        fontSize: "0.95rem",
        fontWeight: 700,
        color: "#1f2937",
        letterSpacing: "-0.01em",
    },
    savedCardsCount: {
        marginLeft: "auto",
        fontSize: "0.75rem",
        fontWeight: 600,
        color: "#6b7280",
        background: "#f3f4f6",
        padding: "0.25rem 0.6rem",
        borderRadius: "20px",
    },
    savedCardsGrid: {
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
    },
    savedCardModern: {
        width: "100%",
        borderRadius: "20px",
        padding: "1rem",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "hidden",
        textAlign: "left",
        fontFamily: "inherit",
    },
    savedCardModernSelected: {
        boxShadow: "0 12px 28px rgba(0, 0, 0, 0.15), 0 0 0 3px rgba(37, 99, 235, 0.3)",
    },
    savedCardModernHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "1rem",
        // Reserve space for the absolute-positioned selected checkmark
        paddingRight: "3rem",
    },
    savedCardModernBrand: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
    },
    savedCardModernChip: {
        width: "32px",
        height: "24px",
        background: "rgba(255, 255, 255, 0.3)",
        borderRadius: "6px",
        marginRight: "0.5rem",
    },
    savedCardModernLast4: {
        fontSize: "1.1rem",
        fontWeight: 700,
        letterSpacing: "0.05em",
        fontFamily: "monospace",
    },
    savedCardModernDetails: {
        display: "flex",
        flexDirection: "column",
        gap: "0.25rem",
    },
    savedCardModernName: {
        fontSize: "0.9rem",
        fontWeight: 600,
        opacity: 0.9,
    },
    savedCardModernExpiry: {
        fontSize: "0.75rem",
        opacity: 0.75,
        fontFamily: "monospace",
    },
    savedCardModernCheck: {
        position: "absolute",
        top: "1rem",
        right: "1rem",
        width: "24px",
        height: "24px",
        background: "#10b981",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontSize: "0.75rem",
        fontWeight: "bold",
    },
    emptyCardsState: {
        textAlign: "center",
        padding: "2rem 1rem",
        background: "linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)",
        borderRadius: "20px",
        border: "2px dashed #e5e7eb",
    },
    emptyCardsIcon: {
        fontSize: "3rem",
        marginBottom: "1rem",
        opacity: 0.6,
    },
    emptyCardsTitle: {
        fontSize: "1rem",
        fontWeight: 700,
        color: "#374151",
        marginBottom: "0.5rem",
    },
    emptyCardsText: {
        fontSize: "0.85rem",
        color: "#6b7280",
        margin: 0,
    },
    cardPreview: {
        background: "linear-gradient(145deg, #111827 0%, #1f2937 50%, #0f766e 100%)",
        borderRadius: "14px",
        padding: "1.1rem 1.15rem",
        color: "#ecfeff",
        marginBottom: "0.9rem",
        boxShadow: "0 10px 20px rgba(15, 23, 42, 0.3)",
    },
    cardPreviewChip: {
        width: "36px",
        height: "28px",
        borderRadius: "6px",
        background: "linear-gradient(160deg, #fde68a, #f59e0b)",
        marginBottom: "0.9rem",
    },
    cardPreviewNumber: {
        fontSize: "1.25rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        marginBottom: "0.8rem",
    },
    cardPreviewMetaRow: {
        display: "flex",
        justifyContent: "space-between",
        fontSize: "0.95rem",
        letterSpacing: "0.05em",
        opacity: 0.95,
    },
    paymentSubmitButton: {
        background: "#2563eb",
        color: "white",
        width: "100%",
        padding: "1.1rem 1rem",
        fontSize: "1.1rem",
        borderRadius: "12px",
        border: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.6rem",
        transition: "all 0.25s ease",
        boxShadow: "0 10px 22px rgba(37, 99, 235, 0.28)",
        fontWeight: 800,
        letterSpacing: "0.01em",
    },
    achAuthorizationCard: {
        marginTop: "0.85rem",
        marginBottom: "0.4rem",
        padding: "0.65rem 0.85rem",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        color: "#334155",
        fontSize: "0.78rem",
        lineHeight: 1.45,
    },
    chargeResultCard: {
        marginTop: "0.95rem",
        background: "#f5fff8",
        border: "1px solid #bbf7d0",
        borderRadius: "12px",
        padding: "0.85rem",
    },
    chargeResultTitle: {
        margin: "0 0 0.45rem 0",
        color: "#166534",
        fontSize: "0.95rem",
        fontWeight: 800,
    },
    subtleHint: {
        color: "#6b7280",
        fontSize: "0.95rem",
        margin: "0.2rem 0 0.85rem 0",
        lineHeight: 1.45,
    },
    timeline: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.8rem",
        padding: "1rem",
        background: "#ffffff",
        borderRadius: "16px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
    },
    timelineStep: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0.5rem",
    },
    timelineDotActive: {
        width: "10px",
        height: "10px",
        background: "#2563eb",
        borderRadius: "50%",
        boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.2)",
    },
    timelineDot: {
        width: "8px",
        height: "8px",
        background: "#d1d5db",
        borderRadius: "50%",
    },
    timelineConnector: {
        flex: 1,
        height: "2px",
        background: "linear-gradient(90deg, #2563eb, #d1d5db)",
        maxWidth: "60px",
    },
    timelineText: {
        fontSize: "0.76rem",
        color: "#64748b",
        fontWeight: 600,
    },
};

// Global animations (injected once via componentDidMount)
const GLOBAL_STYLE_ID = 'art-fee-pay-global-styles';
const globalStyles = `
    @keyframes slideUp {
        0% {
            opacity: 0;
            transform: translateY(30px);
        }
        100% {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
    
    button:hover:not(:disabled) {
        transform: translateY(-2px);
    }
    
    button:active:not(:disabled) {
        transform: translateY(0);
    }
    
    .approveButton:hover:not(:disabled) {
        box-shadow: 0 12px 26px rgba(37, 99, 235, 0.32);
    }
    
    .requestButton:hover:not(:disabled) {
        box-shadow: 0 10px 20px rgba(15, 23, 42, 0.14);
    }

    .saved-card-item:hover {
        transform: translateY(-2px) scale(1.01);
        box-shadow: 0 20px 35px rgba(0, 0, 0, 0.1);
    }

    @media (max-width: 768px) {
        button {
            width: 100%;
            min-width: unset !important;
        }

        .estimate-payment-layout {
            display: block !important;
        }

        .estimate-payment-summary {
            position: static !important;
            margin-top: 0.9rem;
        }
    }

    input:focus {
        border-color: #4f46e5;
        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
    }

`;

type PaymentMethodSelectorProps = {
    paymentMode: "new" | "saved";
    onSelectMode: (mode: "new" | "saved") => void;
};

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({ paymentMode, onSelectMode }) => (
    <div style={styles.paymentMethodTabs}>
        <button
            style={{ ...styles.paymentTab, ...(paymentMode === "new" ? styles.paymentTabActive : {}) }}
            onClick={() => onSelectMode("new")}
        >
            + New card
        </button>
        <button
            style={{ ...styles.paymentTab, ...(paymentMode === "saved" ? styles.paymentTabActive : {}) }}
            onClick={() => onSelectMode("saved")}
        >
            Saved card
        </button>
    </div>
);

type CardFormProps = {
    cardInput: CardInput;
    saveCardForFuture: boolean;
    onInputChange: (field: keyof CardInput, value: string | number) => void;
    onToggleSave: (checked: boolean) => void;
    showFundingFeeHint?: boolean;
};

const CardForm: React.FC<CardFormProps> = ({
    cardInput,
    saveCardForFuture,
    onInputChange,
    onToggleSave,
    showFundingFeeHint,
}) => (
    <div>
        <div style={styles.inputSectionCard}>
            <div style={styles.cardPreview}>
                <div style={styles.cardPreviewChip}></div>
                <div style={styles.cardPreviewNumber}>{cardInput.number || "•••• •••• •••• ••••"}</div>
                <div style={styles.cardPreviewMetaRow}>
                    <span>{cardInput.name || "CARDHOLDER NAME"}</span>
                    <span>
                        {cardInput.expMonth > 0 ? String(cardInput.expMonth).padStart(2, "0") : "MM"}/
                        {cardInput.expYear > 0 ? String(cardInput.expYear).slice(-2) : "YY"}
                    </span>
                </div>
            </div>
            <p style={styles.subtleHint}>Use a new card now. Full card details are never shown after submit.</p>
            <div style={styles.fieldGrid}>
                <InputField label="Card number" value={cardInput.number} placeholder="4111 1111 1111 1111" onChange={(value) => onInputChange("number", value.replace(/[^\d\s]/g, ""))} />
                <InputField label="Cardholder name" value={cardInput.name} placeholder="Full name on card" onChange={(value) => onInputChange("name", value)} />
                <ExpiryField
                    expMonth={cardInput.expMonth}
                    expYear={cardInput.expYear}
                    onChange={(month, year) => {
                        onInputChange("expMonth", month);
                        onInputChange("expYear", year);
                    }}
                />
                <InputField label="CVC" value={cardInput.cvc} placeholder="3-4 digits" onChange={(value) => onInputChange("cvc", value.replace(/[^\d]/g, ""))} />
                <InputField label="Postal code" value={cardInput.postalCode} placeholder="ZIP / postal code" onChange={(value) => onInputChange("postalCode", value)} />
            </div>
            {showFundingFeeHint ? (
                <p style={styles.cardHintMicro}>
                    <strong>Fee tip:</strong> we read the first digits of your card to tell <strong>debit</strong> (no processing fee) from{" "}
                    <strong>credit</strong> or <strong>prepaid</strong> (flat 3%). Your pay button always matches the total shown.
                </p>
            ) : null}
            <label style={styles.checkboxRow}>
                <input type="checkbox" checked={saveCardForFuture} onChange={(e) => onToggleSave(e.currentTarget.checked)} />
                Save this card for future orders
            </label>
        </div>
    </div>
);

type AchFormProps = {
    achInput: {
        accountHolderName: string;
        routingNumber: string;
        accountNumber: string;
        accountType: "checking" | "savings";
    };
    saveBankForFuture: boolean;
    onToggleSaveBank: (checked: boolean) => void;
    onInputChange: (field: "accountHolderName" | "routingNumber" | "accountNumber" | "accountType", value: string) => void;
};

const AchForm: React.FC<AchFormProps> = ({ achInput, saveBankForFuture, onToggleSaveBank, onInputChange }) => (
    <div style={styles.inputSectionCard}>
        <p style={styles.sectionTitle}>eCheck (ACH)</p>
        <p style={styles.subtleHint}>
            No processing fee for eCheck. Bank payments are submitted right away but settle in 1–3 business days.
        </p>
        <div style={styles.fieldGrid}>
            <InputField
                label="Account holder name"
                value={achInput.accountHolderName}
                placeholder="Name on bank account"
                onChange={(value) => onInputChange("accountHolderName", value)}
            />
            <InputField
                label="Routing number"
                value={achInput.routingNumber}
                placeholder="9 digits"
                onChange={(value) => onInputChange("routingNumber", value.replace(/[^\d]/g, ""))}
            />
            <InputField
                label="Account number"
                value={achInput.accountNumber}
                placeholder="Bank account number"
                onChange={(value) => onInputChange("accountNumber", value.replace(/[^\d]/g, ""))}
            />
            <label style={styles.inputWrap}>
                <span style={styles.label}>Account type</span>
                <select
                    style={styles.input}
                    value={achInput.accountType}
                    onChange={(e) => onInputChange("accountType", e.currentTarget.value)}
                >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                </select>
            </label>
        </div>
        <label style={styles.checkboxRow}>
            <input type="checkbox" checked={saveBankForFuture} onChange={(e) => onToggleSaveBank(e.currentTarget.checked)} />
            Save this bank account for future orders
        </label>
    </div>
);

type SavedCardListProps = {
    cards: SavedCard[];
    selectedCardId: string;
    onSelect: (cardId: string) => void;
};

const SavedCardList: React.FC<SavedCardListProps> = ({ cards, selectedCardId, onSelect }) => {
    const getCardBrandDetails = (brand: string) => {
        const brandUpper = (brand || "").toUpperCase();
        const brands: { [key: string]: { icon: any; background: string; iconColor: string; textColor: string } } = {
            VISA: { icon: (FaIcons as any).FaCcVisa, background: "linear-gradient(135deg, #1a1f71 0%, #0a0f4e 100%)", iconColor: "#1a1f71", textColor: "#ffffff" },
            MASTERCARD: { icon: (FaIcons as any).FaCcMastercard, background: "linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)", iconColor: "#eb001b", textColor: "#ffffff" },
            MAST: { icon: (FaIcons as any).FaCcMastercard, background: "linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)", iconColor: "#eb001b", textColor: "#ffffff" },
            AMEX: { icon: (FaIcons as any).FaCcAmex, background: "linear-gradient(135deg, #2e77d0 0%, #1a4f8b 100%)", iconColor: "#2e77d0", textColor: "#ffffff" },
            DISCOVER: { icon: (FaIcons as any).FaCcDiscover, background: "linear-gradient(135deg, #ff6000 0%, #e55c00 100%)", iconColor: "#ff6000", textColor: "#ffffff" },
            DISC: { icon: (FaIcons as any).FaCcDiscover, background: "linear-gradient(135deg, #ff6000 0%, #e55c00 100%)", iconColor: "#ff6000", textColor: "#ffffff" },
        };
        
        for (const [key, details] of Object.entries(brands)) {
            if (brandUpper.includes(key)) return details;
        }
        return { 
            icon: (FaIcons as any).FaCreditCard, 
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            iconColor: "#667eea",
            textColor: "#ffffff" 
        };
    };

    if (!cards.length) {
        return (
            <div style={styles.emptyCardsState}>
                <div style={styles.emptyCardsIcon}>💳</div>
                <p style={styles.emptyCardsTitle}>No saved cards yet</p>
                <p style={styles.emptyCardsText}>Save a card from "New Card" mode to see it here</p>
            </div>
        );
    }

    return (
        <div style={styles.savedCardsContainer}>
            <div style={styles.savedCardsHeader}>
                <span style={styles.savedCardsHeaderIcon}>💳</span>
                <h3 style={styles.savedCardsHeaderTitle}>Your Saved Cards</h3>
                <span style={styles.savedCardsCount}>{cards.length} card{cards.length !== 1 ? 's' : ''}</span>
            </div>
            <div style={styles.savedCardsGrid}>
                {cards.map((card) => {
                    const brandDetails = getCardBrandDetails(card.brand || "");
                    const BrandIcon = brandDetails.icon;
                    const isSelected = selectedCardId === card.cardId;
                    const expMonth = card.expMonth ? String(card.expMonth).padStart(2, "0") : "MM";
                    const expYear = card.expYear ? String(card.expYear).slice(-2) : "YY";
                    const last4 = card.last4 || "••••";
                    
                    return (
                        <button
                            key={card.cardId}
                            type="button"
                            onClick={() => onSelect(card.cardId)}
                            className="saved-card-item"
                            style={{
                                ...styles.savedCardModern,
                                background: isSelected ? brandDetails.background : "#ffffff",
                                border: isSelected ? "none" : "1px solid #e5e7eb",
                                transform: isSelected ? "scale(1.02)" : "scale(1)",
                            }}
                        >
                            <div style={styles.savedCardModernHeader}>
                                <div style={styles.savedCardModernBrand}>
                                    {BrandIcon && <BrandIcon size={32} style={{ color: isSelected ? "#ffffff" : brandDetails.iconColor }} />}
                                </div>
                                <div style={{ ...styles.savedCardModernLast4, color: isSelected ? "#ffffff" : "#1f2937" }}>
                                    •••• {last4}
                                </div>
                            </div>
                            <div style={styles.savedCardModernDetails}>
                                <div style={{ ...styles.savedCardModernName, color: isSelected ? "#ffffff" : "#374151" }}>
                                    {card.displayLabel || "Saved Card"}
                                </div>
                                <div style={{ ...styles.savedCardModernExpiry, color: isSelected ? "rgba(255,255,255,0.8)" : "#6b7280" }}>
                                    Expires {expMonth}/{expYear}
                                </div>
                                <div style={styles.savedCardBadgeRow}>
                                    <span
                                        style={{
                                            ...fundingBadgeStyle(normalizeStoredCardFunding(card.cardFundingType)),
                                            ...(isSelected ? { background: "rgba(255,255,255,0.2)", color: "#fff", borderColor: "rgba(255,255,255,0.35)" } : {}),
                                        }}
                                    >
                                        {fundingLabel(normalizeStoredCardFunding(card.cardFundingType))}
                                    </span>
                                    {normalizeStoredCardFunding(card.cardFundingType) === "unknown" ? (
                                        <span
                                            style={{
                                                fontSize: "0.7rem",
                                                color: isSelected ? "rgba(255,255,255,0.85)" : "#64748b",
                                                fontWeight: 600,
                                            }}
                                        >
                                            Standard fee until verified
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            {isSelected && (
                                <div style={styles.savedCardModernCheck}>
                                    ✓
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

type CardFundingInsightProps = {
    styles: { [key: string]: React.CSSProperties };
    paymentMode: "new" | "saved";
    binPreviewPhase: BinPreviewPhase;
    effectiveFunding: BackendCardFundingType | "unknown";
    subtotal: number;
};

const CardFundingInsight: React.FC<CardFundingInsightProps> = ({
    styles: st,
    paymentMode,
    binPreviewPhase,
    effectiveFunding,
    subtotal,
}) => {
    if (subtotal <= 0) return null;

    const stdFee = subtotal * CARD_PROCESSING_FEE_RATE;
    let title = "How your card total is calculated";
    let body: React.ReactNode;

    if (paymentMode === "saved") {
        title = "Saved card & fees";
        if (effectiveFunding === "Debit") {
            body = (
                <>
                    This card is on file as <strong>debit</strong>. There is <strong>no card processing fee</strong> — you pay only the art design fee (
                    ${subtotal.toFixed(2)}).
                </>
            );
        } else if (effectiveFunding === "unknown") {
            body = (
                <>
                    We don&apos;t have a debit/credit type stored for this card. Your total assumes the <strong>standard card fee</strong> (flat 3% ≈ <strong>
                    ${stdFee.toFixed(2)}</strong>). After payment, the stored type updates for next time.
                </>
            );
        } else {
            body = (
                <>
                    On file as <strong>{effectiveFunding.toLowerCase()}</strong>. Standard processing applies: <strong>flat 3%</strong> (about <strong>
                    ${stdFee.toFixed(2)}</strong> on this order).
                </>
            );
        }
    } else {
        title = "New card & fees";
        if (binPreviewPhase === "short") {
            body = (
                <>
                    Enter at least <strong>8 digits</strong> of your card number. We&apos;ll look up the card range: <strong>debit</strong> = no fee,{" "}
                    <strong>credit / prepaid</strong> = flat 3%.
                </>
            );
        } else if (binPreviewPhase === "loading") {
            body = <>Looking up your card&apos;s fee category from the number you entered…</>;
        } else if (effectiveFunding === "Debit") {
            body = (
                <>
                    This range looks like <strong>debit</strong>. <strong>No card processing fee</strong> — total is the subtotal only (${subtotal.toFixed(2)}
                    ).
                </>
            );
        } else if (binPreviewPhase === "unknown") {
            body = (
                <>
                    We couldn&apos;t read the card type from the BIN. Total assumes <strong>credit/prepaid fees</strong> (~$
                    {stdFee.toFixed(2)}). If you use a debit card, the amount charged will match at payment time.
                </>
            );
        } else {
            body = (
                <>
                    Detected <strong>{effectiveFunding.toLowerCase()}</strong>. Processing fee ≈ <strong>${stdFee.toFixed(2)}</strong> (flat 3%).
                </>
            );
        }
    }

    return (
        <div style={st.fundingCallout}>
            <div style={st.fundingCalloutTitle}>
                <span aria-hidden>✨</span> {title}
            </div>
            <div style={st.fundingCalloutBody}>{body}</div>
        </div>
    );
};

type FeeComparisonPanelProps = {
    paymentRail: "card" | "ach";
    onSelectRail: (rail: "card" | "ach") => void;
};

const FeeComparisonPanel: React.FC<FeeComparisonPanelProps> = ({ paymentRail, onSelectRail }) => (
    <div style={styles.feeComparisonCard}>
        <p style={styles.sectionTitle}>Choose how you pay</p>
        <p style={styles.subtleHint}>Compare card vs bank — totals update automatically based on debit, credit, or ACH.</p>
        <div style={styles.feeComparisonGrid}>
            <button
                style={{ ...styles.feeOptionCard, ...(paymentRail === "card" ? styles.feeOptionCardActive : {}) }}
                onClick={() => onSelectRail("card")}
                type="button"
            >
                <div style={styles.feeOptionTitle}>Debit or credit card</div>
                <div style={styles.feeOptionHighlight}>Debit → no fee</div>
                <div style={styles.feeOptionSub}>Credit &amp; prepaid → flat 3% (shown before you pay).</div>
                <div style={styles.feeOptionHint}>Fast checkout; we detect card type from your number.</div>
            </button>
            <button
                style={{ ...styles.feeOptionCard, ...(paymentRail === "ach" ? styles.feeOptionCardActive : {}) }}
                onClick={() => onSelectRail("ach")}
                type="button"
            >
                <div style={styles.feeOptionTitle}>eCheck (ACH)</div>
                <div style={styles.feeOptionHighlight}>Always $0 fee</div>
                <div style={styles.feeOptionSub}>Pay directly from your bank — no processing fee from us.</div>
                <div style={styles.feeOptionHint}>Best when you want the lowest cost option.</div>
            </button>
        </div>
    </div>
);

type AchModeSelectorProps = {
    achMode: "new" | "saved";
    onSelectMode: (mode: "new" | "saved") => void;
};

const AchModeSelector: React.FC<AchModeSelectorProps> = ({ achMode, onSelectMode }) => (
    <div style={styles.paymentMethodTabs}>
        <button style={{ ...styles.paymentTab, ...(achMode === "new" ? styles.paymentTabActive : {}) }} onClick={() => onSelectMode("new")}>
            New bank account
        </button>
        <button style={{ ...styles.paymentTab, ...(achMode === "saved" ? styles.paymentTabActive : {}) }} onClick={() => onSelectMode("saved")}>
            Saved bank account
        </button>
    </div>
);

type SavedBankAccountListProps = {
    accounts: SavedBankAccount[];
    selectedBankAccountId: string;
    onSelect: (bankAccountId: string) => void;
};

const SavedBankAccountList: React.FC<SavedBankAccountListProps> = ({ accounts, selectedBankAccountId, onSelect }) => (
    <div style={styles.inputSectionCard}>
        {!accounts.length ? (
            <p style={styles.loadingText}>No saved bank accounts yet. Add one in New bank account mode.</p>
        ) : (
            accounts.map((account) => (
                <label
                    key={account.bankAccountId}
                    style={{
                        ...styles.savedCardRow,
                        ...(selectedBankAccountId === account.bankAccountId ? styles.savedCardRowActive : {}),
                    }}
                >
                    <input
                        type="radio"
                        name="saved-bank-account"
                        checked={selectedBankAccountId === account.bankAccountId}
                        onChange={() => onSelect(account.bankAccountId)}
                    />
                    <div style={styles.savedCardLabelWrap}>
                        <span style={{ fontWeight: 700, color: "#1f2937" }}>{account.displayLabel}</span>
                        <span style={styles.savedCardMeta}>Bank account ID: {account.bankAccountId}</span>
                    </div>
                </label>
            ))
        )}
    </div>
);

type AchAuthorizationNoticeProps = {
    amount: number;
};

/**
 * NACHA WEB authorization disclosure. The backend tracks consent columns
 * but does not currently enforce them; the user-facing copy is still our
 * responsibility, so we always show it before any ACH submission.
 */
const AchAuthorizationNotice: React.FC<AchAuthorizationNoticeProps> = ({ amount }) => {
    const amountLabel = amount > 0 ? `$${amount.toFixed(2)}` : "the amount shown above";
    return (
        <div style={styles.achAuthorizationCard}>
            <strong>Authorization:</strong> By clicking <em>Pay {amountLabel}</em>, you authorize The Graphic Cow
            Company to electronically debit your bank account for {amountLabel}. ACH transfers typically settle in
            1–3 business days; you may revoke this authorization in writing before settlement by contacting us.
        </div>
    );
};

type ChargeResultPanelProps = {
    result: ChargeResult | null;
};

const ChargeResultPanel: React.FC<ChargeResultPanelProps> = ({ result }) => {
    if (!result) return null;
    const isAch = result.paymentRail === "ach" || !!result.isPending;
    const title = isAch ? "Bank payment submitted" : "Payment successful";
    return (
        <div style={styles.chargeResultCard}>
            <h4 style={styles.chargeResultTitle}>{title}</h4>
            {isAch ? (
                <div style={styles.featureItem}>
                    Pending settlement — ACH transfers typically settle in 1–3 business days.
                </div>
            ) : null}
            <div style={styles.featureItem}>Intuit charge id: {result.intuitChargeId || "-"}</div>
            <div style={styles.featureItem}>QBO payment id: {result.paymentId || "(pending)"}</div>
        </div>
    );
};

type InputFieldProps = {
    label: string;
    value: string;
    placeholder?: string;
    onChange: (value: string) => void;
};

const InputField: React.FC<InputFieldProps> = ({ label, value, placeholder, onChange }) => (
    <label style={styles.inputWrap}>
        <span style={styles.label}>{label}</span>
        <input style={styles.input} value={value} placeholder={placeholder} onChange={(e) => onChange(e.currentTarget.value)} />
    </label>
);

type ExpiryFieldProps = {
    expMonth: number;
    expYear: number;
    onChange: (month: number, year: number) => void;
};

/**
 * Single MM/YY input. Year is treated as 20YY since QBO won't accept cards
 * from the 1900s and we don't want users typing four digits.
 */
const ExpiryField: React.FC<ExpiryFieldProps> = ({ expMonth, expYear, onChange }) => {
    const initialFromProps = (): string => {
        const m = expMonth > 0 ? String(expMonth).padStart(2, "0") : "";
        const y = expYear > 0 ? String(expYear).slice(-2) : "";
        if (m && y) return `${m}/${y}`;
        return m;
    };

    const [display, setDisplay] = React.useState<string>(initialFromProps);

    React.useEffect(() => {
        const next = initialFromProps();
        // Only sync from props when they would produce a clearly different value
        // (e.g. a reset to 0/0). Avoid clobbering mid-typing state.
        if (!next && expMonth === 0 && expYear === 0 && display !== "") {
            setDisplay("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expMonth, expYear]);

    const handle = (raw: string) => {
        const digits = raw.replace(/\D/g, "").slice(0, 4);
        let formatted = digits;
        if (digits.length >= 3) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        } else if (digits.length === 2 && raw.endsWith("/")) {
            formatted = `${digits}/`;
        }
        setDisplay(formatted);
        const monthNum = digits.length >= 2 ? Number(digits.slice(0, 2)) : 0;
        const yearNum = digits.length === 4 ? 2000 + Number(digits.slice(2)) : 0;
        onChange(monthNum, yearNum);
    };

    return (
        <label style={styles.inputWrap}>
            <span style={styles.label}>Expiration (MM/YY)</span>
            <input
                style={styles.input}
                value={display}
                placeholder="MM/YY"
                inputMode="numeric"
                autoComplete="cc-exp"
                maxLength={5}
                onChange={(e) => handle(e.currentTarget.value)}
            />
        </label>
    );
};

export { ArtFeePay };